import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { ColorResult, SketchPicker } from "react-color";
import { useTranslation } from "react-i18next";
import DraggableDialog from "~/components/DraggableDialog";
import { useAppSelector } from "~/redux/store/store";
import { isNumber } from "~/utils/isNumber";
import { lerp } from "~/utils/lerp";
import {
  addNewColorAndStep,
  adjustStepLimits,
  calculateStepAbsVals,
  CesiumPointSettings,
  ColorAndStep,
  ColoringBlendingMode,
  ColoringBlendingModes,
  ColoringValueMode,
  ColoringValueModes,
  colorsAndStepsToCesium,
  DEFAULT_BLENDING_MODE,
  DEFAULT_COLORS_AND_STEPS,
  DEFAULT_VALUE_MODE,
  SavedElevationSettings,
  SavedIntensitySettings,
  updateColoringSteps,
} from "../../functions/colorAdjustments";

interface Props {
  open: boolean;
  onClose: () => void;
  onChange?: (val: CesiumPointSettings) => void;
  onColorsAndStepsChange?: (val: ColorAndStep[]) => void;
  onElevationStringChange?: (val: string) => void;
  onIntensityStringChange?: (val: string) => void;
  colorCondition: "elevation" | "intensity";
  initialMinValAbs?: number;
  initialMaxValAbs?: number;
  tilesetMinValAbs?: number;
  tilesetMaxValAbs?: number;
  initialColorsAndSteps?: ColorAndStep[];
  initialColoringValueMode?: ColoringValueMode;
  initialColoringBlendingMode?: ColoringBlendingMode;
}

export const ColorAdjustmentDialog = ({
  open,
  onClose,
  onChange,
  onColorsAndStepsChange,
  onElevationStringChange,
  onIntensityStringChange,
  colorCondition,
  initialMinValAbs,
  initialMaxValAbs,
  tilesetMinValAbs,
  tilesetMaxValAbs,
  initialColorsAndSteps = DEFAULT_COLORS_AND_STEPS,
  initialColoringBlendingMode = DEFAULT_BLENDING_MODE,
  initialColoringValueMode = DEFAULT_VALUE_MODE,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const [coloringBlendingMode, setColoringBlendingMode] =
    React.useState<ColoringBlendingMode>(initialColoringBlendingMode);
  const [coloringValueMode, setColoringValueMode] =
    React.useState<ColoringValueMode>(initialColoringValueMode);
  const [colorsAndSteps, setColorsAndSteps] = React.useState<ColorAndStep[]>(
    initialColorsAndSteps,
  );
  const [preEditState, setPreEditState] = React.useState<ColorAndStep[]>();
  const [selectedColorAndStep, setSelectedColorAndStep] =
    React.useState<ColorAndStep["index"]>();
  const [colorAndStepToEdit, setColorAndStepToEdit] =
    React.useState<ColorAndStep["index"]>();
  const [minValAbsInput, setMinValAbsInput] = React.useState(
    initialMinValAbs?.toString() ?? "",
  );
  const [minValAbs, setMinValAbs] = React.useState(initialMinValAbs);
  const [maxValAbsInput, setMaxValAbsInput] = React.useState(
    initialMaxValAbs?.toString() ?? "",
  );
  const [maxValAbs, setMaxValAbs] = React.useState(initialMaxValAbs);
  const isMobile = useAppSelector((x) => x.general.isMobile);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement>();

  React.useEffect(() => {
    if (!onColorsAndStepsChange) return;
    onColorsAndStepsChange(colorsAndSteps);
  }, [colorsAndSteps, onColorsAndStepsChange]);

  const handleColorAdjustClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };
  const handleColorAdjustClose = () => {
    setAnchorEl(undefined);
  };

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleColoringBlendingModeChange = React.useCallback(
    (e: SelectChangeEvent<ColoringBlendingMode>) => {
      setColoringBlendingMode(e.target.value as ColoringBlendingMode);
    },
    [],
  );

  const handleColoringValueModeChange = React.useCallback(
    (e: SelectChangeEvent<ColoringValueMode>) => {
      setColoringValueMode(e.target.value as ColoringValueMode);
    },
    [],
  );

  const handleColorsAndStepsChange = React.useCallback(
    (newColorsAndSteps: ColorAndStep[]) => {
      setColorAndStepToEdit(undefined);
      setColorsAndSteps(
        updateColoringSteps(newColorsAndSteps, minValAbs, maxValAbs),
      );
    },
    [maxValAbs, minValAbs],
  );

  const handleColorsAndStepsAdd = React.useCallback(
    (addAfter?: ColorAndStep) => {
      setColorAndStepToEdit(undefined);
      setColorsAndSteps((prev) => addNewColorAndStep(prev, addAfter));
    },
    [],
  );

  // Calculate abs values for steps
  React.useEffect(() => {
    if (!isNumber(minValAbs) || !isNumber(maxValAbs)) return;
    setColorsAndSteps((prev) =>
      calculateStepAbsVals(prev, minValAbs!, maxValAbs!),
    );
  }, [minValAbs, maxValAbs]);

  const getValueCellText = React.useCallback(
    (v: ColorAndStep) => {
      const pctText = `${v.stepMin.toFixed(2).replace(".00", "")}—${v.stepMax
        .toFixed(2)
        .replace(".00", "")}`;
      const pctUnit = "%";
      const absText = `${
        v.stepMinAbs !== undefined && v.stepMaxAbs !== undefined
          ? `${v.stepMinAbs.toFixed(2)} — ${v.stepMaxAbs.toFixed(2)}`
          : ""
      }`;
      return coloringValueMode === "percentage"
        ? `${pctText}${pctUnit} (${absText})`
        : `${absText} (${pctText}${pctUnit})`;
    },
    [coloringValueMode],
  );

  const handleSingleColorChange = React.useCallback(
    (c: ColorResult) => {
      if (!selectedColorAndStep) return;
      const v = selectedColorAndStep;
      setColorsAndSteps((prev) => [
        ...prev.map((p) => (p.index === v ? { ...p, color: c.hex } : p)),
      ]);
    },
    [selectedColorAndStep],
  );

  const handleStepSliderChange = React.useCallback(
    ({ lowerPct, higherPct, stepIndex }: StepSliderChangeEvent) => {
      setColorsAndSteps((prev) => {
        if (!prev) return prev;
        if (!isNumber(minValAbs) || !isNumber(maxValAbs)) return prev;
        return adjustStepLimits({
          items: prev,
          indexToEdit: stepIndex,
          newHighValue: higherPct,
          newLowValue: lowerPct,
          absMin: minValAbs!,
          absMax: maxValAbs!,
        });
      });
    },
    [maxValAbs, minValAbs],
  );

  const handleRowEditClick = React.useCallback(
    (index: ColorAndStep["index"]) => {
      setPreEditState(colorsAndSteps.map((x) => ({ ...x })));
      setColorAndStepToEdit(index);
    },
    [colorsAndSteps],
  );

  const handleRowEditCancelClick = React.useCallback(() => {
    setColorAndStepToEdit(undefined);
    if (preEditState) setColorsAndSteps(preEditState.map((x) => ({ ...x })));
    setPreEditState(undefined);
  }, [preEditState]);

  const handleRowEditSaveClick = React.useCallback(() => {
    setColorAndStepToEdit(undefined);
    setPreEditState(undefined);
    setColorsAndSteps((prev) => {
      if (!prev || !isNumber(minValAbs) || !isNumber(maxValAbs)) return prev;
      return updateColoringSteps(
        prev.filter((x) => !x.toBeDeleted),
        minValAbs,
        maxValAbs,
      );
    });
  }, [maxValAbs, minValAbs]);

  const handleMinValAbsInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMinValAbsInput(e.target.value);
      if (
        !e.target.value ||
        typeof +e.target.value !== "number" ||
        (maxValAbs && +e.target.value >= maxValAbs)
      )
        return;
      setMinValAbs(+e.target.value);
    },
    [maxValAbs],
  );
  const handleMaxValAbsInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMaxValAbsInput(e.target.value);
      if (
        !e.target.value ||
        typeof +e.target.value !== "number" ||
        (minValAbs && +e.target.value <= minValAbs)
      )
        return;
      setMaxValAbs(+e.target.value);
    },
    [minValAbs],
  );

  const handleDefaultClick = React.useCallback(() => {
    setColoringBlendingMode(DEFAULT_BLENDING_MODE);
    setColoringValueMode(DEFAULT_VALUE_MODE);
    if (tilesetMinValAbs !== undefined) {
      setMinValAbs(tilesetMinValAbs);
      setMinValAbsInput(tilesetMinValAbs.toString());
    }
    if (tilesetMaxValAbs !== undefined) {
      setMaxValAbs(tilesetMaxValAbs);
      setMaxValAbsInput(tilesetMaxValAbs.toString());
    }
    if (tilesetMinValAbs !== undefined && tilesetMaxValAbs !== undefined) {
      setColorsAndSteps(
        calculateStepAbsVals(
          DEFAULT_COLORS_AND_STEPS,
          tilesetMinValAbs,
          tilesetMaxValAbs,
        ),
      );
    }
  }, [tilesetMaxValAbs, tilesetMinValAbs]);

  const renderStepRows = React.useMemo(() => {
    return colorsAndSteps.map((v) =>
      // Highest and lowest are hidden, for now
      v.isLowest || v.isHighest || v.toBeDeleted ? null : (
        <React.Fragment key={v.index}>
          <TableRow>
            <TableCell
              align="right"
              sx={{
                minWidth: 95,
                p: v.isLowest || v.isHighest ? 0.5 : 0,
                border: "none",
              }}
            >
              {colorAndStepToEdit &&
              colorsAndSteps[colorAndStepToEdit]?.index === v.index ? (
                <>
                  <Tooltip title={t("general:save").toString()}>
                    <IconButton
                      sx={{ p: 0.5 }}
                      size="small"
                      onClick={handleRowEditSaveClick}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("general:cancel").toString()}>
                    <IconButton
                      sx={{ p: 0.5 }}
                      size="small"
                      onClick={handleRowEditCancelClick}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title={t("general:edit").toString()}>
                  <IconButton
                    sx={{ p: 0.5 }}
                    size="small"
                    onClick={() => handleRowEditClick(v.index)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
            <TableCell
              align="left"
              sx={{
                p: v.isLowest || v.isHighest ? 0.5 : 0,
                pr: 3,
                border: "none",
              }}
            >
              <Typography
                sx={{
                  width: 200,
                }}
                variant="body2"
              >
                {getValueCellText(v)}
              </Typography>
            </TableCell>
            <TableCell
              align="center"
              sx={{
                p: v.isLowest || v.isHighest ? 0.5 : 0,
                border: "none",
              }}
            >
              <Button
                onClick={(e) => {
                  handleColorAdjustClick(e);
                  setSelectedColorAndStep(v.index);
                }}
                sx={{
                  height: 20,
                  width: 64,
                  backgroundColor: v.color?.toString(),
                  "&:hover": {
                    backgroundColor: v.color?.toString(),
                  },
                  borderRadius: 0,
                  m: "0 auto",
                }}
              />
            </TableCell>
            <TableCell
              align="center"
              sx={{
                p: v.isLowest || v.isHighest ? 0.5 : 0,
                border: "none",
              }}
            >
              {v.isLowest || v.isHighest ? null : (
                <IconButton
                  sx={{ p: 0.5 }}
                  size="small"
                  disabled={colorsAndSteps.length <= 3} // Only first + last + one modifiable value remain
                  onClick={() =>
                    handleColorsAndStepsChange(
                      colorsAndSteps.filter((c) => c !== v),
                    )
                  }
                >
                  <CloseIcon />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
          <EditingRow
            index={v.index}
            items={[...colorsAndSteps]}
            itemToEditIndex={colorAndStepToEdit}
            valueMode={coloringValueMode}
            absMax={maxValAbs}
            absMin={minValAbs}
            onChange={handleStepSliderChange}
          />
          <AddNewStepRow onAdd={() => handleColorsAndStepsAdd(v)} />
        </React.Fragment>
      ),
    );
  }, [
    colorAndStepToEdit,
    coloringValueMode,
    colorsAndSteps,
    getValueCellText,
    handleColorsAndStepsAdd,
    handleColorsAndStepsChange,
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    handleStepSliderChange,
    maxValAbs,
    minValAbs,
    t,
  ]);

  React.useEffect(() => {
    if (!colorsAndSteps) return;
    const cesiumColorSettings = colorsAndStepsToCesium(
      colorsAndSteps,
      colorCondition,
      coloringBlendingMode,
    );
    onChange?.(cesiumColorSettings);
    if (
      colorCondition === "elevation" &&
      minValAbs !== undefined &&
      maxValAbs !== undefined
    ) {
      const elevationSettings: SavedElevationSettings = {
        colorsAndSteps,
        blendingMode: coloringBlendingMode,
        valueMode: coloringValueMode,
        minZ: minValAbs,
        maxZ: maxValAbs,
      };
      const elevationString = JSON.stringify(elevationSettings);
      onElevationStringChange?.(elevationString);
    } else if (
      colorCondition === "intensity" &&
      minValAbs !== undefined &&
      maxValAbs !== undefined
    ) {
      const intensitySettings: SavedIntensitySettings = {
        colorsAndSteps,
        blendingMode: coloringBlendingMode,
        valueMode: coloringValueMode,
        minIntensity: minValAbs,
        maxIntensity: maxValAbs,
      };
      const intensityString = JSON.stringify(intensitySettings);
      onIntensityStringChange?.(intensityString);
    }
  }, [
    onChange,
    colorCondition,
    colorsAndSteps,
    coloringBlendingMode,
    minValAbs,
    maxValAbs,
    coloringValueMode,
    onElevationStringChange,
    onIntensityStringChange,
  ]);

  return (
    <DraggableDialog
      open={open}
      // onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      title={t("color-adjustment")}
      disablePortal={false}
      // keepMounted
      actions={
        <>
          <Button sx={{ mr: 1 }} onClick={handleDefaultClick}>
            {t("set-default")}
          </Button>
          <Button onClick={handleClose}>{t("general:close")}</Button>
        </>
      }
      toolbar={
        <IconButton
          style={{ marginLeft: "auto" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      }
    >
      <>
        {/* Main container */}
        <Grid container>
          {/* First row, selects for blend/steps mode and percentage/absolute */}
          <Grid
            container
            item
            xs={12}
            alignItems="center"
            justifyContent="space-around"
          >
            <Grid item xs={5}>
              <FormControl fullWidth>
                <InputLabel>{t("coloring-value-mode")}</InputLabel>
                <Select
                  value={coloringValueMode}
                  onChange={handleColoringValueModeChange}
                >
                  {ColoringValueModes.map((c) => (
                    <MenuItem value={c} key={c}>
                      {t(`cesium-coloring-value-modes:${c}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth>
                <InputLabel>{t("coloring-mode")}</InputLabel>
                <Select
                  value={coloringBlendingMode}
                  onChange={handleColoringBlendingModeChange}
                >
                  {ColoringBlendingModes.map((c) => (
                    <MenuItem value={c} key={c}>
                      {t(`cesium-coloring-blending-modes:${c}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {/* Second container, contains abs min/max value inputs */}
          <Grid
            container
            sx={{ mt: 1 }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item>
              <TextField
                value={minValAbsInput}
                label={t("min-abs")}
                size="small"
                inputProps={{
                  step: 0.01,
                }}
                type="number"
                onChange={handleMinValAbsInputChange}
              />
            </Grid>
            <Grid item>
              <TextField
                value={maxValAbsInput}
                label={t("max-abs")}
                size="small"
                inputProps={{
                  step: 0.01,
                }}
                type="number"
                onChange={handleMaxValAbsInputChange}
              />
            </Grid>
          </Grid>
          {/* Third container, contains a table with two columns, range and color */}
          <Grid container item xs={12}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{ p: 0, border: "none" }} />
                  <TableCell align="left" sx={{ p: 0, border: "none" }}>
                    {t("value")}
                  </TableCell>
                  <TableCell align="center" sx={{ p: 0, border: "none" }}>
                    {t("color")}
                  </TableCell>
                  <TableCell align="center" sx={{ p: 0, border: "none" }} />
                </TableRow>
              </TableHead>
              <TableBody>
                <AddNewStepRow onAdd={() => handleColorsAndStepsAdd()} />
                {renderStepRows}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleColorAdjustClose}
          onAuxClick={handleColorAdjustClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <SketchPicker
            disableAlpha
            color={
              selectedColorAndStep
                ? colorsAndSteps[selectedColorAndStep]?.color
                : undefined
            }
            onChange={handleSingleColorChange}
          />
        </Popover>
      </>
    </DraggableDialog>
  );
};

type AddNewStepRowProps = {
  onAdd: () => void;
};
function AddNewStepRow({ onAdd }: AddNewStepRowProps): JSX.Element {
  const [hovered, setHovered] = React.useState(false);
  const { t } = useTranslation("cesium");

  const hoveredHeight = 4;
  const normalHeight = 2;

  return (
    <TableRow>
      <TableCell
        colSpan={4}
        onMouseOver={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          padding: 0,
          height: hoveredHeight * 1.5,
          borderBottom: "none",
        }}
      >
        <Tooltip title={t("add-new-coloring-step").toString()}>
          <Button
            onClick={onAdd}
            sx={{
              display: "block",
              width: "100%",
              height: hovered ? hoveredHeight : normalHeight,
              p: 0,
              backgroundColor: (theme) => theme.palette.divider,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.divider,
              },
            }}
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

type StepSliderChangeEvent = {
  lowerPct: number;
  higherPct: number;
  stepIndex: ColorAndStep["index"];
};
type EditingRowProps = {
  index: ColorAndStep["index"];
  items: ColorAndStep[];
  itemToEditIndex: ColorAndStep["index"] | undefined;
  valueMode: ColoringValueMode;
  absMin: number | undefined;
  absMax: number | undefined;
  onChange: (ev: StepSliderChangeEvent) => void;
};
function EditingRow({
  index,
  itemToEditIndex,
  items,
  valueMode,
  absMin,
  absMax,
  onChange,
}: EditingRowProps): JSX.Element {
  const [sliderValues, setSliderValues] = React.useState<
    [number, number] | null
  >(null);
  const [curValueMode, setCurValueMode] =
    React.useState<ColoringValueMode>(valueMode);
  const [lowerInputValue, setLowerInputValue] = React.useState("0");
  const [higherInputValue, setHigherInputValue] = React.useState("0");

  const isEditing = React.useMemo(
    () => !!(itemToEditIndex && items[itemToEditIndex]?.index === index),
    [index, itemToEditIndex, items],
  );

  // Initial value
  React.useEffect(() => {
    if (
      !itemToEditIndex ||
      !items ||
      !(items[itemToEditIndex]?.index === index)
    ) {
      setSliderValues(null);
      return;
    }
    if (sliderValues !== null && curValueMode === valueMode) return;
    const item = items[itemToEditIndex];
    if (
      valueMode === "absolute" &&
      (!isNumber(item.stepMinAbs) || !isNumber(item.stepMaxAbs))
    )
      return;

    setLowerInputValue(
      valueMode === "absolute"
        ? item.stepMinAbs!.toString()
        : item.stepMin.toString(),
    );
    setHigherInputValue(
      valueMode === "absolute"
        ? item.stepMaxAbs!.toString()
        : item.stepMax.toString(),
    );
    setSliderValues(
      valueMode === "absolute"
        ? [item.stepMinAbs!, item.stepMaxAbs!]
        : [item.stepMin, item.stepMax],
    );
    setCurValueMode(valueMode);
  }, [itemToEditIndex, items, index, valueMode, sliderValues, curValueMode]);

  const handleSliderChange = React.useCallback(
    (_event: Event, value: number | number[]) => {
      if (!Array.isArray(value) || value.length !== 2) return;
      setSliderValues(value as [number, number]);
    },
    [],
  );

  React.useEffect(() => {
    if (!sliderValues) return;
    if (valueMode === "absolute" && (!isNumber(absMin) || !isNumber(absMax)))
      return;
    const lower = Math.min(...sliderValues);
    const higher = Math.max(...sliderValues);
    const minPct = 0;
    const maxPct = 100;

    // Always return percentage values
    const lowerPct =
      valueMode === "percentage"
        ? lower
        : lerp({ x: lower, x1: absMin!, x2: absMax!, y1: minPct, y2: maxPct });
    const higherPct =
      valueMode === "percentage"
        ? higher
        : lerp({ x: higher, x1: absMin!, x2: absMax!, y1: minPct, y2: maxPct });
    onChange({ lowerPct, higherPct, stepIndex: index });
  }, [sliderValues, onChange, index, valueMode, absMin, absMax]);

  const sliderMin = React.useMemo(
    () => (valueMode === "absolute" && absMin ? absMin : 0),
    [valueMode, absMin],
  );
  const sliderMax = React.useMemo(
    () => (valueMode === "absolute" && absMax ? absMax : 100),
    [valueMode, absMax],
  );
  const sliderStep = React.useMemo(
    () => (valueMode === "absolute" ? 0.01 : 1),
    [valueMode],
  );

  const handleLowerInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLowerInputValue(e.target.value);
      if (!e.target.value || typeof +e.target.value !== "number") return;
      const lowVal = +e.target.value;
      setSliderValues((prev) => (prev ? [lowVal, prev[1]] : prev));
    },
    [],
  );

  const handleHigherInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setHigherInputValue(e.target.value);
      if (!e.target.value || typeof +e.target.value !== "number") return;
      const highVal = +e.target.value;
      setSliderValues((prev) => (prev ? [prev[0], highVal] : prev));
    },
    [],
  );

  return (
    <TableRow>
      <TableCell
        colSpan={4}
        sx={{
          p: 0,
          border: "none",
        }}
      >
        <Box
          sx={{
            maxHeight: isEditing ? 100 : 0,
            overflow: "hidden",
            transition: (theme) =>
              theme.transitions.create("max-height", {
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          {sliderValues ? (
            <Box sx={{ px: 1, pt: 4, width: "90%", m: "auto" }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Input
                    value={lowerInputValue}
                    size="small"
                    inputProps={{
                      step: sliderStep,
                      min: sliderMin,
                      max: sliderMax,
                    }}
                    type="number"
                    onChange={handleLowerInputChange}
                  />
                </Grid>
                <Grid item xs>
                  <Slider
                    size="small"
                    value={sliderValues}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                  />
                </Grid>
                <Grid item>
                  <Input
                    value={higherInputValue}
                    size="small"
                    inputProps={{
                      step: sliderStep,
                      min: sliderMin,
                      max: sliderMax,
                    }}
                    type="number"
                    onChange={handleHigherInputChange}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  );
}
